import type { ErpModule } from '@/api/platform.api';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { resolveRouteFeature } from '@/utils/navigation';
import { isRouteAllowedForEntryModule, getPostLoginPath } from '@/utils/moduleScope';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';

export type RouteAccessDenial = {
  message: string;
  fallbackPath: string;
};

export function featureDeniedMessage(featureCode: string): string {
  return `Feature not enabled: ${featureCode}. Upgrade your subscription to unlock.`;
}

export function evaluateOrgRouteDenial(params: {
  pathname: string;
  permissions: string[];
  masterAdmin: boolean;
  entryModule: ErpModule | null | undefined;
  scopedModule: ErpModule | null;
  routePermission?: string | string[] | null;
  hasFeature: (code: string) => boolean;
  featuresLoading: boolean;
}): RouteAccessDenial | null {
  const {
    pathname,
    permissions,
    masterAdmin,
    entryModule,
    scopedModule,
    routePermission,
    hasFeature,
    featuresLoading,
  } = params;

  if (masterAdmin) return null;
  if (pathname.startsWith('/org/locked')) return null;

  const revertFallback = () =>
    scopedModule
      ? getPostLoginPath(scopedModule, '/pos/dashboard')
      : entryModule
        ? getPostLoginPath(entryModule, MODULE_HUB_PATH)
        : MODULE_HUB_PATH;

  if (routePermission) {
    const allowed =
      permissions.includes('MASTER_ADMIN') ||
      (Array.isArray(routePermission)
        ? routePermission.some((p) => permissions.includes(p))
        : permissions.includes(routePermission));
    if (!allowed) {
      return {
        message: 'You do not have permission to view this page.',
        fallbackPath: revertFallback(),
      };
    }
  }

  if (
    scopedModule &&
    pathname !== '/onboarding' &&
    !isPlatformAdminRoute(pathname) &&
    !isRouteAllowedForEntryModule(pathname, scopedModule)
  ) {
    return {
      message: 'This page is not available in your current module.',
      fallbackPath: revertFallback(),
    };
  }

  const scopedToEntryModule =
    isPlatformAdminRoute(pathname) ||
    (scopedModule && isRouteAllowedForEntryModule(pathname, scopedModule));

  if (!scopedToEntryModule) {
    const featureCode = resolveRouteFeature(pathname);
    if (
      SUBSCRIPTION_VALIDATION_ENABLED &&
      featureCode &&
      !featuresLoading &&
      !hasFeature(featureCode)
    ) {
      return {
        message: featureDeniedMessage(featureCode),
        fallbackPath: revertFallback(),
      };
    }
  }

  return null;
}

export function evaluatePosRouteDenial(params: {
  pathname: string;
  permissions: string[];
  masterAdmin: boolean;
  routePermission?: string | string[] | null;
  hasFeature: (code: string) => boolean;
  featuresLoading: boolean;
  posRouteFeatures: Record<string, string | string[] | null>;
  entryModule: ErpModule | null | undefined;
}): RouteAccessDenial | null {
  const {
    pathname,
    permissions,
    masterAdmin,
    routePermission,
    hasFeature,
    featuresLoading,
    posRouteFeatures,
    entryModule,
  } = params;

  const revertFallback = () =>
    entryModule ? getPostLoginPath(entryModule, '/org') : '/org';

  if (!masterAdmin && routePermission) {
    const allowed =
      permissions.includes('MASTER_ADMIN') ||
      (Array.isArray(routePermission)
        ? routePermission.some((p) => permissions.includes(p))
        : permissions.includes(routePermission));
    if (!allowed) {
      return {
        message: 'You do not have permission to use the POS terminal.',
        fallbackPath: revertFallback(),
      };
    }
  }

  const posFeature = posRouteFeatures[pathname];
  const featureCode = typeof posFeature === 'string' ? posFeature : null;
  if (
    SUBSCRIPTION_VALIDATION_ENABLED &&
    featureCode &&
    !featuresLoading &&
    !hasFeature(featureCode)
  ) {
    return {
      message: featureDeniedMessage(featureCode),
      fallbackPath: revertFallback(),
    };
  }
  return null;
}

export function evaluateModuleScopeDenial(params: {
  pathname: string;
  scopedModule: ErpModule | null;
}): RouteAccessDenial | null {
  const { pathname, scopedModule } = params;
  if (!scopedModule) return null;
  if (isRouteAllowedForEntryModule(pathname, scopedModule)) return null;
  const home = getPostLoginPath(scopedModule, MODULE_HUB_PATH);
  return {
    message: 'This page is not available in your current module.',
    fallbackPath: home,
  };
}
